<template>
  <div>
    <dev-api-wiring-hint />
    <v-alert
      v-if="legacyTimerRetired"
      type="warning"
      prominent
      border="left"
      class="mx-2 mb-3"
    >
      {{ legacyTimerRetirementMessage }}
      <a
        :href="newKonekoTimerUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="white--text font-weight-bold text-decoration-underline ml-1"
      >
        Open the new time & tasks app
      </a>
      . Play, stop, and new entries are disabled here.
    </v-alert>
    <timer-ex-members-panel />
    <v-alert v-if="timerV1Hint" type="info" dense outlined class="mx-2 mb-2">
      {{ timerV1Hint }}
    </v-alert>
    <timer-page-tool-bar
      :show-copy-button="showCopyButton"
      :date-display="selectedDate"
      :timer-controls-disabled="legacyTimerRetired"
      @copy-button-pressed="toggleClipBoardButton"
      @calendar-date-selected="gotoSelectedDate"
    />
    <timer-page-week-bar
      :change-date="selectedDate"
      @goto-date="gotoSelectedDate"
    />
    <timer-page-table-view
      :timer-list="getTimers"
      :timer-controls-disabled="legacyTimerRetired"
    />
  </div>
</template>

<script>
import TimerPageToolBar from "@/components/Timer/TimerPageToolBar";
import TimerPageWeekBar from "@/components/Timer/TimerPageWeekBar";
import TimerPageTableView from "@/components/Timer/TimerPageTableView";
import TimerExMembersPanel from "@/components/Timer/TimerExMembersPanel";
import DevApiWiringHint from "@/components/DevApiWiringHint.vue";
import DEFAULT_CONSTANTS from "@/Utilites/constants";
import {mapActions, mapGetters, mapMutations} from "vuex";
import {formatClipBoardText} from "@/Utilites/clipBoardHelper";
import {today} from "@/Utilites/timerHelper";
import {strIs} from "@/Utilites/helper";
import {isTimerV1Enabled} from "@/config/timerV1Config";
import {
  getNewKonekoTimerUrl,
  isLegacyTimerRetired,
  legacyTimerRetirementMessage,
} from "@/config/legacyTimerConfig";
import {timerV1CrossDayHint} from "@/services/timerV1Hints";
export default {
  name: "TimerView",
  components: {
    "timer-page-table-view": TimerPageTableView,
    "timer-page-tool-bar": TimerPageToolBar,
    "timer-page-week-bar": TimerPageWeekBar,
    "timer-ex-members-panel": TimerExMembersPanel,
    DevApiWiringHint,
  },
  data() {
    return {
      selectedDate: "",
      todayDate: "",
    };
  },
  watch: {
    checkCurrentStatus(oldVal, newVal) {
      if (oldVal !== newVal && newVal !== "") {
        this.loadTimerData(newVal);
        this.todayDate = newVal;
      }
    },
  },
  computed: {
    ...mapGetters([
      "getTimers",
      "userProfileData",
      "getProjectNameList",
      "getTimerV1Session",
      "isTimerV1Active",
    ]),
    timerV1Hint() {
      if (!isTimerV1Enabled() || !this.isTimerV1Active) return "";
      return timerV1CrossDayHint(
        this.getTimerV1Session,
        this.$store.state.timer.timerLogsList
      );
    },
    legacyTimerRetired() {
      return isLegacyTimerRetired();
    },
    newKonekoTimerUrl() {
      return getNewKonekoTimerUrl();
    },
    legacyTimerRetirementMessage() {
      return legacyTimerRetirementMessage();
    },
    showCopyButton() {
      return this.todayDate > this.selectedDate;
    },
    checkCurrentStatus() {
      return today();
    },
  },
  mounted() {
    this.selectedDate = today();
    this.todayDate = today();
    this.SET_VIEWING_TIMER_LOG_DATE(this.todayDate);
    this.loadTimerData(this.todayDate);
  },
  methods: {
    ...mapActions([
      "showSnackBar",
      "closeSnackBar",
      "fetchProjectNameOnly",
      "READ_TIMER_REQUEST",
      "showLoadingProcess",
      "closeLoading",
    ]),
    ...mapMutations(["SET_VIEWING_TIMER_LOG_DATE"]),
    async gotoSelectedDate(date) {
      this.selectedDate = date;
      this.SET_VIEWING_TIMER_LOG_DATE(date);
      await this.loadTimerData(date);
    },
    async toggleClipBoardButton() {
      await this.copyToClipBoard();
    },
    async copyToClipBoard() {
      const text = formatClipBoardText(
        this.getTimers,
        this.getProjectNameList,
        this.selectedDate
      );
      if (!strIs(text)) {
        return;
      }
      await navigator.clipboard.writeText(text);
      if (text !== "") {
        this.showSnackBar({
          text: "Copied To Clipboard!",
          color: DEFAULT_CONSTANTS.success,
        });
        setTimeout(() => {
          this.closeSnackBar();
        }, DEFAULT_CONSTANTS.timeout);
      }
    },
    async loadTimerData(date) {
      await this.showLoadingProcess();
      try {
        const userId = this.userProfileData.user_id;
        if (date === null) date = today();
        await this.READ_TIMER_REQUEST({userId, date});
        await this.fetchProjectNameOnly();
      } catch (err) {
        await this.showSnackBar({
          text: `${JSON.stringify(err)}`,
          color: DEFAULT_CONSTANTS.error,
        });
        setTimeout(async () => {
          await this.closeSnackBar();
        }, DEFAULT_CONSTANTS.timeout);
      } finally {
        await this.closeLoading();
      }
    },
  },
};
</script>
<style scoped></style>
